import React, { useEffect, useState } from "react";
import ViewPermission from "../../components/roleandAccess/ViewPermission";
import CustomTable from "../../components/customTable/CustomTable";
import DebounceSearch from "../../components/common/DebounceSearch";
import Breadcrumb from "../../components/common/BreadCrumb";
import { Edit, SortDownArrow, Trash } from "../../assets/svg";
import { AddButton, Tooltip } from "../../components/micro/input";
import AddEditModal from "../../components/roleandAccess/AddEditModal";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import { deleteRole, getRoleList } from "../../redux/accounts/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import { toast } from "react-toastify";

const RoleAndAccess = () => {
  const [searchValue, setSearchValue] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewPermissionListPopup, setViewPermissionListPopup] = useState(false);
  const [addEditRolePopup, setAddEditRolePopup] = useState(false);
  const [viewPermissionData, setViewPermissionData] = useState({});
  const [deleteRoleLoad, setDeleteRoleLoad] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const dispatch = useDispatch();

  // Selector
  const { roleList, roleLoading } = useSelector((store) => ({
    // profile
    roleList: store?.accountsData?.getRoleListStatus?.data,
    roleLoading: store?.accountsData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getRoleList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [dispatch, pageCount, pageLimit, searchValue, sortBy, sortDirection]);
  const handleAddRoleClick = () => {
    setViewPermissionData({});
    setAddEditRolePopup(true);
  };

  const handleDeleteRole = () => {
    setDeleteRoleLoad(true);
    dispatch(deleteRole(viewPermissionData?.id)).then((res) => {
      setDeleteRoleLoad(false);
      if (res.type === "deleteRole/fulfilled") {
        toast.success(res?.payload?.message);
        dispatch(
          getRoleList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
        setConfirmDelete(false);
      }
    });
  };

  // Table Heading
  const ColumnHeaders = () => {
    return (
      <>
        <th
          scope="col"
          className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
        >
          No.
        </th>
        <th
          scope="col"
          className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
        >
          <div
            className="flex items-center"
            onClick={() => {
              setSortBy("name");
              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
            }}
          >
            <span>Role Name</span>
            <SortDownArrow
              className={`ml-1 transition-transform duration-300 ${
                sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
              }`}
            />
          </div>
        </th>
        <th
          scope="col"
          className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
        >
          Permission List
        </th>
        <th
          scope="col"
          className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
        >
          Action
        </th>
      </>
    );
  };

  // Table Row
  const DataRows = () => {
    return (
      <>
        {(roleList?.data?.data || [])?.map((element, index) => (
          <tr
            className={
              " text-sm shadow-sm text-mgrey hover:bg-lightprimaryorange font-Proxima"
            }
            key={element.id}
          >
            <td className="px-5 py-3 truncate">
              {(roleList?.data.current_page - 1) * roleList?.data.per_page +
                index +
                1}
            </td>
            <td className="px-5 py-3 truncate">
              {element?.title
                ? element?.title
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())
                : "-"}
            </td>
            <td className="px-5 py-3 truncate">
              <button
                className="text-themeBtn border-b border-b-lightOrangeTheme"
                type="button"
                onClick={() => {
                  setViewPermissionListPopup(true);
                  setViewPermissionData(element);
                }}
              >
                View
              </button>
            </td>
            <td className="px-4 py-2">
              <div className="flex items-center space-x-3">
                <Tooltip
                  label={
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setAddEditRolePopup(true);
                          setViewPermissionData(element);
                        }}
                      >
                        <Edit />
                      </button>
                    </>
                  }
                  tooltipLabel={
                    <>
                      <div className="whitespace-nowrap ">
                        <p>Edit </p>
                      </div>
                    </>
                  }
                />
                <Tooltip
                  label={
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmDelete(true);
                          setViewPermissionData(element);
                        }}
                      >
                        <Trash />
                      </button>
                    </>
                  }
                  tooltipLabel={
                    <>
                      <div className="whitespace-nowrap ">
                        <p>Delete </p>
                      </div>
                    </>
                  }
                />
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <>
      <div className={`relative ${viewPermissionListPopup ? "blur-sm" : ""}`}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <h6 className="font-Montserrat font-medium text-2xl mb-3 text-primaryBlack">
              Role & Access
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Role & Access"}
              breadCrumbParent2={"Accounts"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
          <AddButton value="Add Role" onClick={handleAddRoleClick} />
        </div>
        <div className="my-9">
          <DebounceSearch
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>
        {roleLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white  shadow rounded-lg">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              overflowVisibleLg={true}
              data={roleList?.data}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              total={roleList?.data?.total}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(roleList?.data?.total / pageLimit)}
            />
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmationModel
          confirmText={"Delete"}
          isLoading={deleteRoleLoad}
          onClose={() => {
            setConfirmDelete(false);
          }}
          onConfirm={handleDeleteRole}
        />
      )}
      {viewPermissionListPopup && (
        <div>
          <ViewPermission
            viewPermissionData={viewPermissionData}
            setViewPermissionListPopup={setViewPermissionListPopup}
            setAddEditRolePopup={setAddEditRolePopup}
          />
        </div>
      )}
      {addEditRolePopup && (
        <div>
          <AddEditModal
            viewPermissionData={viewPermissionData}
            setAddEditRolePopup={setAddEditRolePopup}
            searchValue={searchValue}
            pageCount={pageCount}
            pageLimit={pageLimit}
            sortBy={sortBy}
            sortDirection={sortDirection}
          />
        </div>
      )}
    </>
  );
};

export default RoleAndAccess;
